import * as bootstrap from "bootstrap";
import { showToast } from "../common";
import { postJson } from "../api";
import { initSheetFileDropzoneApp } from "../vue/sheetFileDropzoneApp";

const PREVIEW_MODAL_ID = "sheet-file-preview-modal";

let previewState = {
  items: [],
  index: -1,
  returnModalEl: null,
};

function isInteractiveElement(element) {
  return element instanceof HTMLElement && ["BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(element.tagName);
}

function getPreviewItems(groupName, fallbackLink = null) {
  const group = String(groupName || "").trim();

  if (group !== "") {
    return Array.from(
      document.querySelectorAll(`[data-sheet-file-preview="1"][data-sheet-file-preview-group="${group}"]`),
    ).filter((link) => link instanceof HTMLAnchorElement);
  }

  return fallbackLink instanceof HTMLAnchorElement ? [fallbackLink] : [];
}

function createPreviewModal() {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = `
    <div class="modal fade sheet-file-preview-modal" id="${PREVIEW_MODAL_ID}" tabindex="-1" data-bs-backdrop="static" aria-labelledby="${PREVIEW_MODAL_ID}-title" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable sheet-file-preview-modal__dialog">
        <div class="modal-content">
          <div class="modal-header align-items-start gap-3">
            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-outline-secondary btn-sm" data-sheet-file-preview-nav="prev" aria-label="Poprzedni plik">
                <i class="bi bi-arrow-left"></i>
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" data-sheet-file-preview-nav="next" aria-label="Następny plik">
                <i class="bi bi-arrow-right"></i>
              </button>
            </div>
            <div class="sheet-file-preview-modal__toolbar min-w-0 flex-grow-1">
              <div class="small text-uppercase text-muted">Podgląd pliku</div>
              <h2 class="visually-hidden" id="${PREVIEW_MODAL_ID}-title">Podgląd pliku <div class="small text-muted mt-1" data-sheet-file-preview-counter hidden></div></h2>
              <select class="form-select form-select-sm sheet-file-preview-modal__picker mt-1" data-sheet-file-preview-picker aria-label="Wybierz plik do podglądu" disabled>
                <option value="">Podgląd pliku</option>
              </select>

            </div>

            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Zamknij"></button>
          </div>
          <div class="modal-body p-0">
            <div class="sheet-file-preview-modal__viewer">
              <iframe class="sheet-file-preview-modal__frame" data-sheet-file-preview-frame title="Podgląd pliku" loading="lazy" hidden></iframe>
              <img class="sheet-file-preview-modal__image" data-sheet-file-preview-image alt="Podgląd pliku" hidden>
            </div>
          </div>
          <div class="modal-footer justify-content-between gap-2">
            <div class="small text-muted" data-sheet-file-preview-note>Podgląd jest dostępny dla plików PDF i obrazów.</div>
            <div class="d-flex gap-2">
              <a class="btn btn-primary" data-sheet-file-preview-download href="#" target="_blank" rel="noopener noreferrer">
                <i class="bi bi-download me-1"></i> Pobierz
              </a>
              <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Zamknij</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  const modalEl = wrapper.firstElementChild;
  if (!(modalEl instanceof HTMLElement)) {
    throw new Error("Nie udało się utworzyć modalu podglądu plików.");
  }

  document.body.appendChild(modalEl);
  return modalEl;
}

function ensurePreviewModal() {
  let modalEl = document.getElementById(PREVIEW_MODAL_ID);

  if (!(modalEl instanceof HTMLElement)) {
    modalEl = createPreviewModal();
  }

  if (modalEl.dataset.sheetFilePreviewInit === "1") {
    return modalEl;
  }

  modalEl.dataset.sheetFilePreviewInit = "1";

  modalEl.addEventListener("hidden.bs.modal", () => {
    const frame = modalEl.querySelector("[data-sheet-file-preview-frame]");
    const image = modalEl.querySelector("[data-sheet-file-preview-image]");
    const picker = modalEl.querySelector("[data-sheet-file-preview-picker]");
    if (frame instanceof HTMLIFrameElement) {
      frame.src = "about:blank";
    }
    if (image instanceof HTMLImageElement) {
      image.src = "";
      image.alt = "Podgląd pliku";
    }
    if (picker instanceof HTMLSelectElement) {
      picker.innerHTML = '<option value="">Podgląd pliku</option>';
      picker.value = "";
      picker.disabled = true;
    }

    const returnModalEl = previewState.returnModalEl;
    previewState = {
      items: [],
      index: -1,
      returnModalEl: null,
    };

    if (returnModalEl instanceof HTMLElement && document.body.contains(returnModalEl)) {
      bootstrap.Modal.getOrCreateInstance(returnModalEl).show();
    }
  });

  const picker = modalEl.querySelector("[data-sheet-file-preview-picker]");
  if (picker instanceof HTMLSelectElement) {
    picker.addEventListener("change", () => {
      const nextIndex = Number.parseInt(picker.value, 10);
      if (Number.isInteger(nextIndex)) {
        setPreviewIndex(nextIndex);
      }
    });
  }

  return modalEl;
}

function updatePreviewModal() {
  const modalEl = ensurePreviewModal();
  const counterEl = modalEl.querySelector("[data-sheet-file-preview-counter]");
  const pickerEl = modalEl.querySelector("[data-sheet-file-preview-picker]");
  const frameEl = modalEl.querySelector("[data-sheet-file-preview-frame]");
  const imageEl = modalEl.querySelector("[data-sheet-file-preview-image]");
  const downloadEl = modalEl.querySelector("[data-sheet-file-preview-download]");
  const noteEl = modalEl.querySelector("[data-sheet-file-preview-note]");
  const prevButton = modalEl.querySelector('[data-sheet-file-preview-nav="prev"]');
  const nextButton = modalEl.querySelector('[data-sheet-file-preview-nav="next"]');
  const currentItem = previewState.items[previewState.index] ?? null;

  if (
    !(pickerEl instanceof HTMLSelectElement) ||
    !(frameEl instanceof HTMLIFrameElement) ||
    !(imageEl instanceof HTMLImageElement) ||
    !(downloadEl instanceof HTMLAnchorElement)
  ) {
    return;
  }

  pickerEl.innerHTML = "";
  if (previewState.items.length === 0) {
    const emptyOption = document.createElement("option");
    emptyOption.value = "";
    emptyOption.textContent = "Podgląd pliku";
    pickerEl.append(emptyOption);
    pickerEl.disabled = true;
  } else {
    previewState.items.forEach((item, index) => {
      const option = document.createElement("option");
      option.value = String(index);
      option.textContent = item.name;
      pickerEl.append(option);
    });
    pickerEl.disabled = false;
  }

  if (!currentItem) {
    pickerEl.value = "";
    frameEl.src = "about:blank";
    frameEl.hidden = true;
    imageEl.src = "";
    imageEl.alt = "Podgląd pliku";
    imageEl.hidden = true;
    downloadEl.href = "#";
    downloadEl.removeAttribute("download");
    if (noteEl instanceof HTMLElement) {
      noteEl.textContent = "Podgląd jest dostępny dla plików PDF i obrazów.";
    }
    if (counterEl instanceof HTMLElement) {
      counterEl.hidden = true;
      counterEl.textContent = "";
    }
    if (prevButton instanceof HTMLButtonElement) {
      prevButton.disabled = true;
    }
    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = true;
    }
    return;
  }

  pickerEl.value = String(previewState.index);
  downloadEl.href = currentItem.downloadUrl;
  downloadEl.setAttribute("download", currentItem.name);
  downloadEl.setAttribute("aria-label", `Pobierz ${currentItem.name}`);

  if (currentItem.previewType === "image") {
    frameEl.src = "about:blank";
    frameEl.hidden = true;
    imageEl.src = currentItem.previewUrl;
    imageEl.alt = currentItem.name;
    imageEl.hidden = false;
    if (noteEl instanceof HTMLElement) {
      noteEl.textContent = "Podgląd obrazu w pełnym rozmiarze.";
    }
  } else {
    frameEl.src = currentItem.previewUrl;
    frameEl.hidden = false;
    imageEl.src = "";
    imageEl.alt = currentItem.name;
    imageEl.hidden = true;
    if (noteEl instanceof HTMLElement) {
      noteEl.textContent = "Podgląd dokumentu PDF w osadzonym czytniku przeglądarki.";
    }
  }

  if (counterEl instanceof HTMLElement) {
    const hasManyItems = previewState.items.length > 1;
    counterEl.hidden = !hasManyItems;
    counterEl.textContent = hasManyItems ? `${previewState.index + 1} z ${previewState.items.length}` : "";
  }

  if (prevButton instanceof HTMLButtonElement) {
    prevButton.disabled = previewState.index <= 0;
  }

  if (nextButton instanceof HTMLButtonElement) {
    nextButton.disabled = previewState.index >= previewState.items.length - 1;
  }
}

function setPreviewIndex(nextIndex) {
  if (!Array.isArray(previewState.items) || previewState.items.length === 0) {
    return;
  }

  if (nextIndex < 0 || nextIndex >= previewState.items.length) {
    return;
  }

  previewState.index = nextIndex;
  updatePreviewModal();
}

function openPreview(link) {
  if (!(link instanceof HTMLAnchorElement)) {
    return;
  }

  const previewItems = getPreviewItems(link.dataset.sheetFilePreviewGroup, link).map((item) => ({
    previewUrl: item.href,
    downloadUrl: item.href,
    previewType: String(item.dataset.sheetFilePreviewType || "pdf").trim() || "pdf",
    name: String(item.dataset.sheetFileName || item.textContent || "plik").trim(),
    element: item,
  }));

  if (previewItems.length === 0) {
    return;
  }

  previewState.items = previewItems;
  previewState.index = Math.max(
    0,
    previewItems.findIndex((item) => item.element === link),
  );

  const previewModalEl = ensurePreviewModal();
  const parentModalEl = link.closest(".modal.show");
  previewState.returnModalEl =
    parentModalEl instanceof HTMLElement && parentModalEl !== previewModalEl ? parentModalEl : null;

  updatePreviewModal();

  if (previewState.returnModalEl) {
    previewState.returnModalEl.addEventListener(
      "hidden.bs.modal",
      () => bootstrap.Modal.getOrCreateInstance(previewModalEl).show(),
      { once: true },
    );
    bootstrap.Modal.getOrCreateInstance(previewState.returnModalEl).hide();
    return;
  }

  bootstrap.Modal.getOrCreateInstance(previewModalEl).show();
}

function bindPreviewEvents() {
  if (document.documentElement.dataset.sheetFilesPreviewBound === "1") {
    return;
  }

  document.documentElement.dataset.sheetFilesPreviewBound = "1";

  document.addEventListener("click", (event) => {
    const previewLink = event.target instanceof Element ? event.target.closest('[data-sheet-file-preview="1"]') : null;
    if (previewLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      openPreview(previewLink);
      return;
    }

    const navButton = event.target instanceof Element ? event.target.closest("[data-sheet-file-preview-nav]") : null;
    if (!(navButton instanceof HTMLButtonElement)) {
      return;
    }

    event.preventDefault();
    const direction = navButton.dataset.sheetFilePreviewNav;
    if (direction === "prev") {
      setPreviewIndex(previewState.index - 1);
      return;
    }

    if (direction === "next") {
      setPreviewIndex(previewState.index + 1);
    }
  });

  document.addEventListener("keydown", (event) => {
    const modalEl = document.getElementById(PREVIEW_MODAL_ID);
    if (
      !(modalEl instanceof HTMLElement) ||
      !modalEl.classList.contains("show") ||
      isInteractiveElement(event.target)
    ) {
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setPreviewIndex(previewState.index - 1);
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      setPreviewIndex(previewState.index + 1);
    }
  });
}

function setButtonPendingState(button, pending) {
  if (!(button instanceof HTMLElement)) {
    return;
  }

  if (pending) {
    button.dataset.pendingDelete = "1";
    button.setAttribute("aria-disabled", "true");
    button.classList.add("disabled");
    return;
  }

  delete button.dataset.pendingDelete;
  button.removeAttribute("aria-disabled");
  button.classList.remove("disabled");
}

export function initSheetFiles(root = document) {
  if (!(root instanceof Document || root instanceof Element)) {
    return;
  }

  initSheetFileDropzoneApp(root);

  root.querySelectorAll("form[data-sheet-file-auto-submit='1']").forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.sheetFileAutoSubmitInit === "1") {
      return;
    }

    form.dataset.sheetFileAutoSubmitInit = "1";
    form.addEventListener("cea:file-dropzone:files-selected", (event) => {
      if (!event.detail?.files?.length) {
        return;
      }

      form.requestSubmit();
    });
  });

  if (root.querySelector('[data-sheet-file-preview="1"]')) {
    ensurePreviewModal();
    bindPreviewEvents();
  }

  root.querySelectorAll(".btn_delete_file[data-file-delete-url]").forEach((button) => {
    if (!(button instanceof HTMLElement) || button.dataset.sheetFilesInit === "1") {
      return;
    }

    button.dataset.sheetFilesInit = "1";

    button.addEventListener("click", async (event) => {
      event.preventDefault();

      if (button.dataset.pendingDelete === "1") {
        return;
      }

      const deleteUrl = String(button.dataset.fileDeleteUrl || "").trim();
      const csrfToken = String(button.dataset.csrfToken || "").trim();
      const fileName = String(button.dataset.fileName || "plik").trim();

      if (!deleteUrl) {
        return;
      }

      const confirmed = window.confirm(`Usunąć plik: ${fileName}?`);
      if (!confirmed) {
        return;
      }

      setButtonPendingState(button, true);

      try {
        const payload = await postJson(deleteUrl, {}, csrfToken);
        showToast(payload?.message || "Plik został usunięty.", { type: "success" });

        if (payload?.reloadPage) {
          window.location.reload();
        }
      } catch (error) {
        showToast(String(error?.message || error || "Nie udało się usunąć pliku."), { type: "danger" });
      } finally {
        setButtonPendingState(button, false);
      }
    });
  });
}
